/**
 * Startup queries and mutations.
 *
 * The stage mutation applies its move OPTIMISTICALLY: dragging a card across a
 * kanban board and watching it snap back for 200ms while a request resolves
 * makes the board feel broken, and PRD §11 budgets one second for the whole
 * interaction. The rollback on failure is what makes that safe.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md tasks 6.1, 6.2, 6.9
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type {
  ChangeStageRequest,
  CheckDuplicatesQuery,
  CreateStartupRequest,
  DuplicateMatch,
  ListStartupsQuery,
  SaveScorecardRequest,
  Scorecard,
  StartupSummary,
  UpdateStartupRequest,
} from '@nexus/contracts';

import { queryKeys } from '../../../lib/query-keys';

import * as startupsApi from './startups.api';
import type { StartupDetail, StartupPage, StageChangeResult } from './startups.api';

export function useStartups(query: ListStartupsQuery): UseQueryResult<StartupPage> {
  return useQuery({
    queryKey: queryKeys.startups.list(query),
    queryFn: () => startupsApi.listStartups(query),
  });
}

/**
 * Probable duplicates of a name and/or website (F14).
 *
 * Disabled until there is something to check, so the intake form fires no
 * request on an empty field. Advisory only — the result never blocks a create.
 */
export function useStartupDuplicates(
  query: CheckDuplicatesQuery | null,
): UseQueryResult<{ duplicates: DuplicateMatch[] }> {
  const effective: CheckDuplicatesQuery = query ?? {};

  return useQuery({
    queryKey: queryKeys.startups.duplicates(query ?? {}),
    queryFn: () => startupsApi.checkDuplicates(effective),
    enabled: query !== null,
  });
}

export function useStartup(id: string): UseQueryResult<StartupDetail> {
  return useQuery({
    queryKey: queryKeys.startups.detail(id),
    queryFn: () => startupsApi.getStartup(id),
  });
}

export function useCreateStartup(): UseMutationResult<
  StartupSummary,
  Error,
  CreateStartupRequest
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: CreateStartupRequest) => startupsApi.createStartup(body),
    onSuccess: () => {
      /* Every list is invalidated, not just the current filter set: a new
         startup may match filters the user is not looking at right now, and a
         stale count in another tab is the kind of thing nobody reports. */
      void queryClient.invalidateQueries({ queryKey: queryKeys.startups.all });
    },
  });
}

export function useUpdateStartup(
  id: string,
): UseMutationResult<StartupSummary, Error, UpdateStartupRequest> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: UpdateStartupRequest) => startupsApi.updateStartup(id, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.startups.all });
    },
  });
}

/** What the optimistic stage update saved, so it can be rolled back. */
interface StageMutationContext {
  /** Every list cache entry touched, with its previous contents. */
  previous: [readonly unknown[], StartupPage][];
}

/** A stage change: which startup, and the change to apply. */
export interface StageChangeVariables extends ChangeStageRequest {
  startupId: string;
}

/**
 * Moves a startup to another stage.
 *
 * The startup id travels in the VARIABLES rather than being bound at hook
 * creation: the board moves whichever card the user drags, and a hook keyed by
 * one id cannot be called per card without breaking the rules of hooks.
 *
 * The move is applied to every cached list immediately and rolled back if the
 * request fails. A 409 or a 422 both roll back — the caller then renders the
 * conflict dialog (task 6.9) or the unmet-criteria warnings (task 6.7).
 */
export function useChangeStage(): UseMutationResult<
  StageChangeResult,
  Error,
  StageChangeVariables,
  StageMutationContext
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ startupId, ...body }: StageChangeVariables) =>
      startupsApi.changeStage(startupId, body),

    onMutate: ({ startupId, stage }): StageMutationContext => {
      /* Cancelled first: an in-flight refetch that resolves after this write
         would overwrite the optimistic value with the server's old one. */
      void queryClient.cancelQueries({ queryKey: queryKeys.startups.all });

      /* Filtered to entries that hold data: `getQueriesData` also returns
         queries still in flight, whose data is undefined, and writing an
         optimistic page into one of those would invent a page the user never
         fetched. */
      const entries = queryClient
        .getQueriesData<StartupPage>({ queryKey: queryKeys.startups.lists() })
        .filter(
          (entry): entry is [readonly unknown[], StartupPage] => entry[1] !== undefined,
        );

      for (const [key, page] of entries) {
        queryClient.setQueryData<StartupPage>(key, {
          ...page,
          data: page.data.map((startup) =>
            startup.id === startupId ? { ...startup, stage } : startup,
          ),
        });
      }

      return { previous: entries };
    },

    onError: (_error, _variables, context) => {
      /* Restored exactly as it was. Re-fetching instead would leave the card in
         the wrong column until the request came back, which looks like the
         move half-worked.

         TanStack types `context` as possibly undefined because onMutate is
         optional in general — but ours is defined and always returns, so
         onError cannot be reached without it. Asserted rather than guarded: a
         guard would be a branch no test could enter. */
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- see above
      for (const [key, page] of context!.previous) {
        queryClient.setQueryData(key, page);
      }
    },

    onSettled: () => {
      /* Reconciled either way: on success the server may have changed more
         than the stage (stageEnteredAt, cleared parking fields), and on
         failure the rollback needs confirming against the server. */
      void queryClient.invalidateQueries({ queryKey: queryKeys.startups.all });
    },
  });
}

export function useScorecard(startupId: string): UseQueryResult<Scorecard> {
  return useQuery({
    queryKey: queryKeys.startups.scorecard(startupId),
    queryFn: () => startupsApi.getScorecard(startupId),
  });
}

export function useSaveScorecard(
  startupId: string,
): UseMutationResult<Scorecard, Error, SaveScorecardRequest> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: SaveScorecardRequest) =>
      startupsApi.saveScorecard(startupId, body),
    onSuccess: (saved) => {
      /* Written straight into the cache: the response IS the new scorecard, so
         a refetch would be a second round trip for data already in hand. */
      queryClient.setQueryData(queryKeys.startups.scorecard(startupId), saved);

      /* Lists still need invalidating — saving a scorecard can change the
         band, which lists sort by. */
      void queryClient.invalidateQueries({ queryKey: queryKeys.startups.all });
    },
  });
}
